/**
 * La lecture du catalogue public.
 *
 * Une seule source : la vue `v_stages_publics`. Pas de requête sur `sessions`
 * depuis ces pages — la vue est une liste blanche de colonnes, et c'est sa
 * raison d'être. Interroger la table directement rouvrirait la porte que la
 * migration `source_publique_des_stages` a fermée.
 *
 * Aucune de ces fonctions n'exige de session : elles s'exécutent avec la clé
 * publique, ce qui est le point — un robot n'a pas de compte.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { typeDe, type StagePublic } from './stage';

/**
 * La vue, interrogée par un client délibérément non typé.
 *
 * `database.types.ts` est GÉNÉRÉ depuis la base, et `v_stages_publics` n'y
 * figure pas. Deux voies s'offraient : l'y déclarer à la main — mais la
 * prochaine régénération l'écraserait, et la déclaration se tairait le jour où
 * la vue change — ou reconnaître que le type n'existe pas et le dire.
 *
 * C'est la seconde, parce que la vérification que le type aurait faite est
 * faite ci-dessous par `versStagePublic`, à l'exécution, sur la vraie ligne.
 * Un type qui ment est pire qu'un type absent.
 *
 * À régénérer les types un jour, cette indirection disparaîtra d'elle-même.
 */
const vue = () => (supabase as unknown as SupabaseClient).from('v_stages_publics');

/*
  Les journées pédagogiques sont des sessions comme les autres pour la base,
  mais pas des stages : elles s'inscrivent par leur propre page, réservées aux
  élèves d'une école. Le catalogue public les tait.
*/
const JPA = 'jpa';

/**
 * Une ligne de la vue, convertie en `StagePublic`.
 *
 * Une conversion explicite plutôt qu'un `as` : la base rend `type_activite`
 * comme une chaîne — c'est un `text` avec une contrainte, pas une énumération
 * PostgreSQL — et rien ne garantit côté TypeScript qu'elle vaille l'une des
 * trois valeurs attendues. `typeDe` la ramène à `stage` si elle sort du cadre,
 * ce qui vaut mieux qu'une page qui afficherait « Ce undefined est destiné
 * aux enfants de… ».
 *
 * C'est aussi le seul endroit où la forme de la base rencontre celle de
 * l'application : un `as` l'aurait dispersée partout.
 */
function versStagePublic(ligne: Record<string, unknown>): StagePublic {
  return {
    ...(ligne as unknown as StagePublic),
    type_activite: typeDe(ligne as unknown as StagePublic),
  };
}

export class ErreurDeCatalogue extends Error {
  constructor(brut: string | null | undefined) {
    const nettoye = (brut ?? '').trim();
    super(nettoye || 'Le catalogue n’a pas pu être lu.');
    this.name = 'ErreurDeCatalogue';
  }
}

/** Tout ce qui est publiable, trié comme une famille le lirait. */
export async function stagesPublics(): Promise<StagePublic[]> {
  const { data, error } = await vue()
    .select('*')
    .neq('type_activite', JPA)
    .order('start_date', { ascending: true })
    .order('stage', { ascending: true })
    .order('centre', { ascending: true });

  if (error) throw new ErreurDeCatalogue(error.message);
  return (data ?? []).map(versStagePublic);
}

/**
 * Un stage par son adresse.
 *
 * Rend `null` plutôt que de lever quand l'adresse n'existe pas : une page
 * inconnue est un cas normal du web, pas une panne. C'est l'appelant qui
 * décide d'afficher « ce stage n'est plus proposé » et de proposer la suite.
 */
export async function stageParAdresse(slug: string): Promise<StagePublic | null> {
  const { data, error } = await vue()
    .select('*')
    .eq('slug', slug)
    .neq('type_activite', JPA)
    .maybeSingle();

  if (error) throw new ErreurDeCatalogue(error.message);
  return data ? versStagePublic(data) : null;
}

/**
 * Les stages voisins d'un stage donné, pour le maillage interne (§18).
 *
 * Trois familles de voisins, dans cet ordre de pertinence : la même commune,
 * la même période, la même tranche d'âge. On en rend peu — un bloc de liens
 * qui reprend tout le catalogue ne maille rien, il dilue.
 */
export async function stagesVoisins(
  stage: StagePublic,
  limite = 6,
): Promise<StagePublic[]> {
  const { data, error } = await vue()
    .select('*')
    .neq('slug', stage.slug)
    .neq('type_activite', JPA)
    .or(
      [
        stage.city ? `city.eq.${stage.city}` : null,
        stage.periode_code ? `periode_code.eq.${stage.periode_code}` : null,
      ]
        .filter(Boolean)
        .join(','),
    )
    .order('start_date', { ascending: true })
    .limit(limite);

  if (error) throw new ErreurDeCatalogue(error.message);
  return (data ?? []).map(versStagePublic);
}
